import { legeUnterlageAb, type FreigabeFesthalten } from '../../../services/unterlagenAblage';
import { mitZeitlimit } from '../../../services/ablageHilfen';
import * as service from '../../../services/bauordnungService';
import type { SchreibKontext } from '../../../services/bauordnungService';
import { fetchProjektMetadaten } from '../../../services/asplanService';
import { renderBauordnungPdf } from '../../../services/pdf/renderBauordnungPdf';
import type { ReportMetadata } from '../../../types';
import { baueDokument, bauordnungDateiname, type BauKontext } from '../dokument/baueDokument';
import { DOKUMENTE, type Dokumentart } from '../kapitel';
import type { Bauordnung, BauordnungBundle } from '../types';
import type { ProjektMerkmale } from '../../planungsphase/types';

/**
 * Ablage der freigegebenen Fassung in der zentralen Projektablage
 * (`project_documents`, Modul `baustellenordnung`).
 *
 * ═══ WANN ABGELEGT WIRD ═══════════════════════════════════════════════════
 * Automatisch bei der FREIGABE — und nur dann (Plan Stufe 1A.3, Entscheid
 * E17). Ein Entwurf laesst sich drucken, landet aber nicht in der Ablage:
 * sonst fuellt sie sich mit Zwischenstaenden, und hinterher weiss niemand
 * mehr, nach welchem Blatt gearbeitet wurde.
 *
 * ═══ DIE FREIGABE HAENGT NICHT AN DER ABLAGE ══════════════════════════════
 * Erst wird freigegeben (ein Update, `freigebenBauordnung`), dann abgelegt.
 * Scheitert die Ablage — Storage weg, Datei zu gross, Netz gestoert —, bleibt
 * die Freigabe gueltig: der Stand ist eingefroren, das Dokument laesst sich
 * weiterhin drucken. Die Ansicht sagt, was fehlt, und bietet „Erneut ablegen"
 * an. Die Alternative — Freigabe zuruecknehmen, weil die Ablage hakt —
 * machte die Rechtsfolge einer Freigabe von einem Dateiserver abhaengig.
 *
 * ═══ ZWEI DOKUMENTE, EINE FASSUNG ═════════════════════════════════════════
 * Baustellenordnung und HSE-Plan entstehen aus EINEM Bestand und werden bei
 * EINER Freigabe eingefroren. Beide bekommen deshalb dieselbe Fassung
 * (`freigegeben_am`), aber je eine eigene Kennung (`freigabeKennung`), denn
 * in der Ablage sind es zwei Dateien. Die Ids der abgelegten Zeilen stehen am
 * Kopfsatz (`dokument_id_ordnung` / `dokument_id_hse`) — das ist der Anker,
 * den Aenderungsdienst und Uebergabe spaeter lesen.
 *
 * ═══ AUSGABE FUER DEN AENDERUNGSDIENST (Stufe 6.1): DER ANKER GEHOERT ZUR
 *     FREIGABE ══════════════════════════════════════════════════════════════
 * Der Steckbrief-Auszug, gegen den spaetere Aenderungen der Planungsphase
 * verglichen werden, wird IM MOMENT DER FREIGABE festgehalten
 * (`baueBauordnungFreigabeAuftrag` → `halteFreigabeFest` des Kerns): je
 * erzeugtem Dokument eine Ausgabe (Anlass `freigabe`) unter derselben
 * Kennung, unter der das Blatt spaeter abgelegt wird (`freigabeKennung`),
 * mit leerer `dokument_id`. Die Stammdaten (`metadata`) gibt der Aufrufer
 * mit; hat er sie nicht, werden sie EINMAL gelesen — mit Zeitlimit. Bleiben
 * sie aus, bildet der Kern KEINEN Anker (leere Projekt- und Bauherrendaten
 * meldeten spaeter Abweichungen, die es nie gab) und sagt es. Scheitert das
 * Journal, bleibt die Freigabe gueltig; der Grund steht an der Freigabe, und
 * mit dem Anker des Versuchs laesst es sich dort wiederholen.
 *
 * Die ABLAGE bildet nie einen Anker (`anker: null`): „Erneut ablegen" kann
 * nach einem Reiterwechsel oder Neuladen Tage spaeter kommen, und ein
 * heutiger Stand fuer eine alte Freigabe verdeckte echte Abweichungen
 * (Codex-Review 17.10.2026). Der Kern prueft nur, ob zur Kennung ein Anker
 * liegt; fehlt er (Journal damals gescheitert, Altbestand vor Nr. 163), sagt
 * sein Hinweis, dass die Fassung nicht ueberwacht wird. Was der Kern anmerkt
 * (`hinweise`), kommt gesammelt zurueck — es ist kein Fehler, die Fassung liegt.
 *
 * ═══ WAS HIER NICHT STEHT ═════════════════════════════════════════════════
 * Keine Word-Ausgabe: `bauordnungDateiname` kennt zwar die Endung `docx`,
 * einen Word-Generator gibt es im Repo aber nicht. Abgelegt wird das PDF —
 * dasselbe, das der Knopf „PDF" herunterlaedt.
 */

// ═══════════════════════════════════════════════════════════════════════════
// REINE REGELN — ohne Datenbank pruefbar
// ═══════════════════════════════════════════════════════════════════════════

/** Die Spalte des Kopfsatzes, die die Dokument-Id EINES Dokuments traegt. */
export const DOKUMENT_ID_SPALTE: Record<Dokumentart, 'dokument_id_ordnung' | 'dokument_id_hse'> = {
    ordnung: 'dokument_id_ordnung',
    hse: 'dokument_id_hse',
};

type Ausgabesteuerung = Pick<Bauordnung, 'erzeugt_ordnung' | 'erzeugt_hse'>;
type Ablagelage = Ausgabesteuerung & Pick<Bauordnung, 'status' | 'dokument_id_ordnung' | 'dokument_id_hse'>;

/** Die Dokumente, die diese Baustellenordnung ueberhaupt erzeugt. */
export const erzeugteDokumente = (o: Ausgabesteuerung): Dokumentart[] => {
    const arten: Dokumentart[] = [];
    if (o.erzeugt_ordnung) arten.push('ordnung');
    if (o.erzeugt_hse) arten.push('hse');
    return arten;
};

/**
 * Welche Dokumente der freigegebenen Fassung noch NICHT in der Ablage liegen.
 *
 * Fuer einen Entwurf immer leer: er wird nicht abgelegt. Ein Dokument gilt
 * als abgelegt, sobald seine Id am Kopfsatz steht — die Freigabe leert beide
 * Ids, deshalb zeigt ein stehengebliebener Verweis nie auf eine alte Fassung.
 */
export const offeneAblagen = (o: Ablagelage): Dokumentart[] => {
    if (o.status !== 'freigegeben') return [];
    return erzeugteDokumente(o).filter(art => !o[DOKUMENT_ID_SPALTE[art]]);
};

/**
 * Der Freigabezeitpunkt in EINER Schreibweise.
 *
 * Der Client schreibt `toISOString()` („…00.123Z"), PostgREST liest
 * „…00.123+00:00" zurueck. Beides ist derselbe Augenblick; als Kennung muss
 * er aber gleich geschrieben sein, sonst erkennt die Ablage eine zweite
 * Ablage derselben Fassung nicht als dieselbe. Unlesbares bleibt, wie es ist.
 */
const normierterZeitpunkt = (iso: string): string => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toISOString();
};

/**
 * Die Fassungskennung EINES Dokuments (`fassung_ref` in der Ablage).
 *
 * Je Dokumentart eine eigene: Baustellenordnung und HSE-Plan derselben
 * Freigabe sind zwei Dateien. Ohne Freigabezeitpunkt gibt es keine Kennung —
 * ein Entwurf hat keine Fassung.
 */
export const freigabeKennung = (art: Dokumentart, freigegebenAm: string | null | undefined): string | null => {
    const am = String(freigegebenAm || '').trim();
    return am ? `${art}@${normierterZeitpunkt(am)}` : null;
};

const tagDeutsch = (iso?: string | null): string => {
    const t = String(iso || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '';
    const [j, m, d] = t.split('-');
    return `${d}.${m}.${j}`;
};

type Fassungskopf = Pick<Bauordnung, 'version_label' | 'freigegeben_am'>;

/**
 * Der Titel, unter dem ein Dokument in der Ablage steht.
 *
 * Fassung und Freigabetag gehoeren IN den Titel (Muster `planAblageTitel` des
 * A+S-Plans): die Ablage sortiert nach Titel, und fuenfmal „Baustellenordnung"
 * untereinander laesst sich nicht lesen.
 */
export const ablageTitel = (art: Dokumentart, o: Fassungskopf): string => {
    const fassung = String(o.version_label || '').trim() || 'ohne Fassungsbezeichnung';
    const tag = tagDeutsch(o.freigegeben_am);
    const zusatz = [fassung, tag ? `freigegeben ${tag}` : ''].filter(Boolean).join(', ');
    return `${DOKUMENTE[art].titel} (${zusatz})`;
};

/** Der Stand, wie er auf dem Dokument steht — Fassung, sonst das Datum. */
export const ablageStand = (o: Fassungskopf & Pick<Bauordnung, 'stand'>): string | undefined => {
    const fassung = String(o.version_label || '').trim();
    if (fassung) return fassung;
    const tag = tagDeutsch(o.stand);
    return tag ? `Stand ${tag}` : undefined;
};

export const ablageBeschreibung = (
    art: Dokumentart,
    o: Pick<Bauordnung, 'erlassender_firma' | 'katalog_version'>,
): string => [
    DOKUMENTE[art].untertitel,
    o.erlassender_firma ? `erlassen durch ${o.erlassender_firma}` : '',
    o.katalog_version ? `Katalogfassung ${o.katalog_version}` : '',
].filter(Boolean).join(' · ');

// ═══════════════════════════════════════════════════════════════════════════
// ERGEBNIS UND MELDUNG
// ═══════════════════════════════════════════════════════════════════════════

export interface FreigabeAblage {
    /** Dokumente, deren Id jetzt vorliegt (frisch abgelegt oder schon vorhanden). */
    abgelegt: Dokumentart[];
    /** Je Dokument der Grund, warum es nicht in der Ablage liegt. */
    fehler: { art: Dokumentart; text: string }[];
    /** Die Ids liegen vor, konnten aber nicht am Kopfsatz vermerkt werden. */
    verknuepfungFehler: string | null;
    /**
     * Anmerkungen des Kerns zu den abgelegten Fassungen (Journal-Eintrag,
     * Steckbrief) — kein Fehler. Nur gesetzt, wenn es welche gab; ohne
     * Doppelte, denn zwei Dokumente derselben Freigabe sagen oft dasselbe.
     */
    hinweise?: string[];
}

/** Leere und doppelte Saetze weg — was bleibt, zeigt die Ansicht dezent. */
const sammleHinweise = (listen: ReadonlyArray<readonly string[] | null | undefined>): string[] =>
    Array.from(new Set(listen.flatMap(l => l || []).map(h => (h || '').trim()).filter(Boolean)));

/**
 * Was die Ansicht nach dem Ablegen sagt.
 *
 * `ton` entscheidet die Farbe: gruen nur, wenn alles liegt UND vermerkt ist.
 * Ein teilweiser Erfolg ist gelb — die Freigabe gilt, aber ein Dokument fehlt
 * in der Ablage, und das soll niemand fuer gruen halten.
 *
 * Hinweise des Kerns haengen hinten am Satz, als „Hinweis: …". Sie aendern
 * den Ton nie: sie entstehen nur zu Fassungen, die liegen, also nie im roten
 * Fall — und ein Hinweis ist kein Grund, Gruen in Gelb zu verwandeln.
 */
export const ablageMeldung = (erg: FreigabeAblage): { ton: 'ok' | 'teils' | 'fehler'; text: string } => {
    const titel = (a: Dokumentart) => DOKUMENTE[a].titel;
    const fehlerText = erg.fehler.map(f => `${titel(f.art)}: ${f.text}`).join('; ');
    const hinweise = sammleHinweise([erg.hinweise]);
    const mitHinweis = (text: string): string => (hinweise.length ? `${text} Hinweis: ${hinweise.join(' ')}` : text);

    if (!erg.abgelegt.length) {
        return {
            ton: 'fehler',
            text: erg.fehler.length
                ? `Nicht in der Projektablage abgelegt. ${fehlerText} Die Freigabe gilt trotzdem; bitte „Erneut ablegen".`
                : 'Es gab nichts abzulegen.',
        };
    }
    const liegt = `${erg.abgelegt.map(titel).join(' und ')} ${erg.abgelegt.length === 1 ? 'liegt' : 'liegen'} in der Projektablage.`;
    if (erg.verknuepfungFehler) {
        return {
            ton: 'teils',
            text: mitHinweis(`${liegt} Der Verweis am Kopfsatz fehlt aber: ${erg.verknuepfungFehler}`),
        };
    }
    if (erg.fehler.length) {
        return {
            ton: 'teils',
            text: mitHinweis(`${liegt} Nicht abgelegt: ${fehlerText} Die Freigabe gilt trotzdem; bitte „Erneut ablegen".`),
        };
    }
    return { ton: 'ok', text: mitHinweis(liegt) };
};

// ═══════════════════════════════════════════════════════════════════════════
// DER ANSCHLUSS — PDF bauen, ablegen, verknuepfen
// ═══════════════════════════════════════════════════════════════════════════

const fehlertext = (e: unknown): string => {
    if (e instanceof Error) return e.message;
    if (typeof e === 'string' && e.trim()) return e;
    return 'unbekannter Fehler';
};

/** Baut das PDF eines Dokuments aus dem FREIGEGEBENEN Buendel und gibt die Bytes aus. */
const pdfAlsBlob = (bundle: BauordnungBundle, art: Dokumentart, kontext: BauKontext): Blob => {
    const dok = baueDokument(bundle, art, kontext);
    const pdf = renderBauordnungPdf(dok, { fusszeileLinks: bundle.ordnung?.erlassender_firma });
    return pdf.output('blob') as Blob;
};

export interface FreigabeAblageArgs {
    /** Das Buendel MIT dem freigegebenen Kopfsatz — nicht das Entwurfs-Buendel. */
    bundle: BauordnungBundle;
    merkmale: ProjektMerkmale;
    projektName?: string;
    projektNummer?: string;
    projektAdresse?: string;
    ctx: SchreibKontext;
    /** Nur fuer Tests: ersetzt den PDF-Bau, damit kein jsPDF laeuft. */
    __pdf?: (bundle: BauordnungBundle, art: Dokumentart, kontext: BauKontext) => Blob;
}

/**
 * Die Ausgabe, die der Kern bei der Ablage bekommt (Stufe 6.1): die Unterlage
 * und `anker: null` — der Anker liegt seit der Freigabe im Journal
 * (Dateikopf). Stammdaten braucht der Kern ohne Anker nicht.
 */
export interface BauordnungAusgabe {
    unterlage: 'baustellenordnung';
    metadata: null;
    anker: null;
}

export const BAUORDNUNG_AUSGABE: BauordnungAusgabe = { unterlage: 'baustellenordnung', metadata: null, anker: null };

/**
 * Die Stammdaten fuer den Auszug der Freigabe: mitgegeben, sonst einmal
 * gelesen — mit Zeitlimit. Scheitert oder haengt das Lesen, bleibt es bei
 * `null`; der Kern bildet dann keinen Anker und sagt warum. Die Freigabe
 * darf an einer Nebenabfrage nie haengen.
 */
const stammdatenFuerAuszug = async (
    projectId: string,
    mitgegeben: Partial<ReportMetadata> | null | undefined,
    zeitlimitMs?: number,
): Promise<Partial<ReportMetadata> | null> => {
    if (mitgegeben !== undefined) return mitgegeben;
    try {
        return await mitZeitlimit(fetchProjektMetadaten(projectId), zeitlimitMs);
    } catch {
        return null;
    }
};

/**
 * Der Auftrag an das Freigabe-Journal des Kerns (`halteFreigabeFest`) — je
 * erzeugtem Dokument eine Kennung (`freigabeKennung`), derselbe Auszug fuer
 * beide. Zu bilden unmittelbar nach `freigebenBauordnung`, aus dem
 * FREIGEGEBENEN Kopfsatz. Die Ansicht behaelt ihn fuer „Erneut festhalten".
 */
export const baueBauordnungFreigabeAuftrag = async (
    o: Bauordnung,
    metadata?: Partial<ReportMetadata> | null,
    zeitlimitMs?: number,
): Promise<FreigabeFesthalten> => ({
    projectId: o.project_id,
    unterlage: 'baustellenordnung',
    fassungRefs: erzeugteDokumente(o).map(art => freigabeKennung(art, o.freigegeben_am)),
    metadata: await stammdatenFuerAuszug(o.project_id, metadata, zeitlimitMs),
});

/**
 * Legt die noch fehlenden Dokumente der freigegebenen Fassung ab und vermerkt
 * ihre Ids am Kopfsatz.
 *
 * Wirft nie. Jedes Dokument wird einzeln versucht: scheitert der HSE-Plan,
 * liegt die Baustellenordnung trotzdem und wird trotzdem vermerkt. Was
 * fehlt, steht im Ergebnis — die Ansicht bietet dafuer „Erneut ablegen".
 *
 * Ein zweiter Anlauf ist gefahrlos: er nimmt nur die Dokumente ohne Id
 * (`offeneAblagen`), und dieselbe `fassungRef` laesst die Ablage eine schon
 * vorhandene Zeile zurueckgeben (`schon_abgelegt`) statt eine zweite
 * anzulegen. Deren Id wird uebernommen — so heilt sich ein Lauf, bei dem die
 * Datei durchging und nur der Vermerk fehlte.
 */
export const legeFreigabeAb = async (args: FreigabeAblageArgs): Promise<FreigabeAblage> => {
    const o = args.bundle.ordnung;
    const leer: FreigabeAblage = { abgelegt: [], fehler: [], verknuepfungFehler: null };
    if (!o) return leer;
    const offen = offeneAblagen(o);
    if (!offen.length) return leer;

    const kontext: BauKontext = {
        projektName: args.projektName,
        projektNummer: args.projektNummer,
        projektAdresse: args.projektAdresse,
        merkmale: args.merkmale,
    };
    const bauePdf = args.__pdf ?? pdfAlsBlob;
    const ids: Partial<Record<Dokumentart, string>> = {};
    const fehler: FreigabeAblage['fehler'] = [];
    const hinweise: Array<readonly string[] | null | undefined> = [];

    for (const art of offen) {
        let datei: Blob;
        try {
            datei = bauePdf(args.bundle, art, kontext);
        } catch (e) {
            fehler.push({ art, text: `Das PDF konnte nicht erzeugt werden: ${fehlertext(e)}` });
            continue;
        }

        try {
            const erg = await legeUnterlageAb({
                projectId: o.project_id,
                modul: 'baustellenordnung',
                datei,
                dateiname: bauordnungDateiname(art, args.projektName || 'Projekt', o.stand),
                titel: ablageTitel(art, o),
                stand: ablageStand(o),
                fassungRef: freigabeKennung(art, o.freigegeben_am),
                anlass: 'freigabe',
                beschreibung: ablageBeschreibung(art, o),
                // Kein eigener Anker: der liegt seit der Freigabe im Journal (Dateikopf).
                ausgabe: BAUORDNUNG_AUSGABE,
            });
            if (erg.status === 'fehler' || !erg.dokument) {
                fehler.push({ art, text: fehlertext(erg.fehler) });
                continue;
            }
            ids[art] = erg.dokument.id;
            // Hinweise nur zu Fassungen, die liegen — zu einem Fehler gibt es
            // keinen Journal-Eintrag, also auch nichts anzumerken.
            hinweise.push(erg.hinweise);
        } catch (e) {
            // Der Ablageweg verspricht, nicht zu werfen. Falls doch, bleibt die
            // Freigabe trotzdem gueltig — und der Grund wird gezeigt.
            fehler.push({ art, text: fehlertext(e) });
        }
    }

    const abgelegt = Object.keys(ids) as Dokumentart[];
    let verknuepfungFehler: string | null = null;
    if (abgelegt.length) {
        try {
            await service.verknuepfeFreigabeDokumente(o, ids, args.ctx);
        } catch (e) {
            verknuepfungFehler = fehlertext(e);
        }
    }

    const gesammelt = sammleHinweise(hinweise);
    return { abgelegt, fehler, verknuepfungFehler, ...(gesammelt.length ? { hinweise: gesammelt } : {}) };
};
